import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { Login } from "./pages/Login";
import { Register } from "./pages/Register";
import { ForgotPassword } from "./pages/ForgotPassword";
import { ResetPassword } from "./pages/ResetPassword";
import { Dashboard } from "./pages/Dashboard";
import { Customers } from "./pages/Customers";
import { CustomerDetail } from "./pages/CustomerDetail";
import { CustomerForm } from "./pages/CustomerForm";
import { Settings } from "./pages/Settings";
import { CalendarPage } from "./pages/Calendar";
import { Engineers } from "./pages/Engineers";
import { UserManagement } from "./pages/UserManagement";
import { EngineerPortal } from "./pages/EngineerPortal";
import { SupportInbox } from "./pages/SupportInbox";
import { PortalAccountsAdmin } from "./pages/PortalAccountsAdmin";
import { AdminBilling } from "./pages/AdminBilling";
import { AdminBackups } from "./pages/AdminBackups";
import { AdminTimeSheets } from "./pages/AdminTimeSheets";
import { HrDirectory } from "./pages/HrDirectory";
import { HrEmployeeDetail } from "./pages/HrEmployeeDetail";
import { HrMyProfile } from "./pages/HrMyProfile";
import { EmployeeApprovals } from "./pages/EmployeeApprovals";
import { MetaLeads } from "./pages/MetaLeads";
import WebsiteChat from "./pages/WebsiteChat";
import { ArchivePage } from "./pages/ArchivePage";
import { Reporting } from "./pages/Reporting";
import { Notifications } from "./pages/Notifications";
import { CustomerPortalLogin } from "./pages/CustomerPortalLogin";
import { CustomerPortalRegister } from "./pages/CustomerPortalRegister";
import { CustomerPortalDashboard } from "./pages/CustomerPortalDashboard";
import PortalPaymentSuccess from "./pages/PortalPaymentSuccess";
import { PortalSetupPassword } from "./pages/PortalSetupPassword";
import { EmailAnalytics } from "./pages/EmailAnalytics";
import { CallAnalytics } from "./pages/CallAnalytics";
import { WebsiteAnalytics } from "./pages/WebsiteAnalytics";
import { PipelineAutomation } from "./pages/PipelineAutomation";
import { EngineerLogin } from "./pages/EngineerLogin";
import { EngineerApp } from "./pages/EngineerApp";
import { Monitoring } from "./pages/Monitoring";
import TrustpilotReviews from "./pages/TrustpilotReviews";
import { PrivacyPolicy } from "./pages/PrivacyPolicy";
import { AuditLog } from "./pages/AuditLog";
import { SocialInbox } from "./pages/SocialInbox";
import SocialNotifications from "./pages/SocialNotifications";
import { PortalChat } from "./pages/PortalChat";
import { WixLeads } from "./pages/WixLeads";
import WhatsAppBusiness from "./pages/WhatsAppBusiness";
import Contacts from "./pages/Contacts";
import PermissionsPage from "./pages/PermissionsPage";
import { PostcodeChecker } from "./pages/PostcodeChecker";
import { EmailLeads } from "./pages/EmailLeads";
import PublicSignVariation from "./pages/PublicSignVariation";
import PublicSignContract from "./pages/PublicSignContract";
import PublicDnoRequest from "./pages/PublicDnoRequest";
import { ErrorBoundary } from "./components/ErrorBoundary";
import HelpfulLinks from "./pages/HelpfulLinks";
import AccessAudit from "./pages/AccessAudit";
import StockPage from "./pages/StockPage";
import HandoverPage from "./pages/HandoverPage";
import RamsOperationsPage from "./pages/RamsOperationsPage";
import { AdhocRams } from "./pages/AdhocRams";
import GdprRetention from "./pages/GdprRetention";
import ContractSetup from "./pages/ContractSetup";
import AdminContractPacks from "./pages/AdminContractPacks";
import AdminProductCatalog from "./pages/AdminProductCatalog";
import AdminHies from "./pages/AdminHies";
import { LeadsArchive } from "./pages/LeadsArchive";
import DNOApplications from "./pages/DNOApplications";
import DnoInbox from "./pages/DnoInbox";
import { SurveyForm } from "./pages/SurveyForm";
import { Surveys } from "./pages/Surveys";
import { NeedsLinking } from "./pages/NeedsLinking";
import { PortalErrorBoundary } from "./components/PortalErrorBoundary";
import { CrmErrorBoundary } from "./components/CrmErrorBoundary";
import { SmartSchedule } from "./pages/SmartSchedule";
import StripePayments from "./pages/StripePayments";

import { ThemeProvider } from "./contexts/ThemeContext";
import { CookieConsent } from "./components/CookieConsent";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <CrmErrorBoundary>{children}</CrmErrorBoundary>;
};

// Public Route Component (redirect if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Root landing: staff go to their dashboard, everyone else lands on the
// customer portal login (the default face of app.helioses.co.uk).
const RootRedirect = () => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  return <Navigate to={isAuthenticated ? '/dashboard' : '/portal/login'} replace />;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <Register />
          </PublicRoute>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <PublicRoute>
            <ForgotPassword />
          </PublicRoute>
        }
      />
      <Route
        path="/reset-password"
        element={
          <PublicRoute>
            <ResetPassword />
          </PublicRoute>
        }
      />

      {/* Protected Routes */}
      <Route path="/engineer-login" element={<EngineerLogin />} />
      <Route path="/engineer-app" element={<ErrorBoundary><EngineerApp /></ErrorBoundary>} />
      <Route path="/sign-variation/:token" element={<PublicSignVariation />} />
      <Route path="/sign-contract/:token" element={<PublicSignContract />} />
      <Route path="/dno-request/:token" element={<PublicDnoRequest />} />
      <Route path="/helpful-links" element={<ProtectedRoute><HelpfulLinks /></ProtectedRoute>} />

      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/customers"
        element={
          <ProtectedRoute>
            <Customers />
          </ProtectedRoute>
        }
      />
      <Route
        path="/customers/new"
        element={
          <ProtectedRoute>
            <CustomerForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/customers/:id"
        element={
          <ProtectedRoute>
            <CustomerDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/customers/:id/edit"
        element={
          <ProtectedRoute>
            <CustomerForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/postcode-checker"
        element={
          <ProtectedRoute>
            <PostcodeChecker />
          </ProtectedRoute>
        }
      />
      <Route
        path="/archive"
        element={
          <ProtectedRoute>
            <ArchivePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/calendar"
        element={
          <ProtectedRoute>
            <CalendarPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/engineers"
        element={
          <ProtectedRoute>
            <Engineers />
          </ProtectedRoute>
        }
      />
      <Route
        path="/user-management"
        element={
          <ProtectedRoute>
            <UserManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/stripe-payments"
        element={
          <ProtectedRoute>
            <StripePayments />
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-jobs"
        element={
          <ProtectedRoute>
            <ErrorBoundary><EngineerPortal /></ErrorBoundary>
          </ProtectedRoute>
        }
      />
      <Route
        path="/support"
        element={
          <ProtectedRoute>
            <SupportInbox />
          </ProtectedRoute>
        }
      />
      <Route
        path="/portal-accounts"
        element={
          <ProtectedRoute>
            <PortalAccountsAdmin />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/billing"
        element={
          <ProtectedRoute>
            <AdminBilling />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/backups"
        element={
          <ProtectedRoute>
            <AdminBackups />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/time-sheets"
        element={
          <ProtectedRoute>
            <AdminTimeSheets />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/access-audit"
        element={
          <ProtectedRoute>
            <AccessAudit />
          </ProtectedRoute>
        }
      />
      <Route
        path="/stock"
        element={
          <ProtectedRoute>
            <StockPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/handover"
        element={
          <ProtectedRoute>
            <HandoverPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/operations/rams"
        element={
          <ProtectedRoute>
            <RamsOperationsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/installs/adhoc-rams"
        element={
          <ProtectedRoute>
            <AdhocRams />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/gdpr-retention"
        element={
          <ProtectedRoute>
            <GdprRetention />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/contract-setup"
        element={
          <ProtectedRoute>
            <ContractSetup />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/contract-packs"
        element={
          <ProtectedRoute>
            <AdminContractPacks />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/product-catalog"
        element={
          <ProtectedRoute>
            <AdminProductCatalog />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/hies"
        element={
          <ProtectedRoute>
            <AdminHies />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/hardware-presets"
        element={
          <ProtectedRoute>
            <ContractSetup />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/manufacturer-warranties"
        element={
          <ProtectedRoute>
            <ContractSetup />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/battery-presets"
        element={
          <ProtectedRoute>
            <ContractSetup />
          </ProtectedRoute>
        }
      />
      <Route path="/hr" element={<ProtectedRoute><HrDirectory /></ProtectedRoute>} />
      <Route path="/hr/me" element={<ProtectedRoute><HrMyProfile /></ProtectedRoute>} />
      <Route path="/hr/employees/:id" element={<ProtectedRoute><HrEmployeeDetail /></ProtectedRoute>} />
      <Route
        path="/employee-approvals"
        element={
          <ProtectedRoute>
            <EmployeeApprovals />
          </ProtectedRoute>
        }
      />
      <Route
        path="/meta-leads"
        element={
          <ProtectedRoute>
            <MetaLeads />
          </ProtectedRoute>
        }
      />
      <Route
        path="/survey-form"
        element={
          <ProtectedRoute>
            <SurveyForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/surveys"
        element={
          <ProtectedRoute>
            <Surveys />
          </ProtectedRoute>
        }
      />
      <Route
        path="/needs-linking"
        element={
          <ProtectedRoute>
            <NeedsLinking />
          </ProtectedRoute>
        }
      />
      <Route
        path="/smart-schedule"
        element={
          <ProtectedRoute>
            <SmartSchedule />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reporting"
        element={
          <ProtectedRoute>
            <Reporting />
          </ProtectedRoute>
        }
      />
      <Route
        path="/notifications"
        element={
          <ProtectedRoute>
            <Notifications />
          </ProtectedRoute>
        }
      />
      <Route
        path="/call-analytics"
        element={
          <ProtectedRoute>
            <CallAnalytics />
          </ProtectedRoute>
        }
      />
      <Route
        path="/email-analytics"
        element={
          <ProtectedRoute>
            <EmailAnalytics />
          </ProtectedRoute>
        }
      />
      <Route
        path="/website-analytics"
        element={
          <ProtectedRoute>
            <WebsiteAnalytics />
          </ProtectedRoute>
        }
      />
      <Route
        path="/pipeline-automation"
        element={
          <ProtectedRoute>
            <PipelineAutomation />
          </ProtectedRoute>
        }
      />
      <Route
        path="/monitoring"
        element={
          <ProtectedRoute>
            <Monitoring />
          </ProtectedRoute>
        }
      />
      <Route
        path="/trustpilot-reviews"
        element={
          <ProtectedRoute>
            <TrustpilotReviews />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dno-applications"
        element={
          <ProtectedRoute>
            <DNOApplications />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dno-inbox"
        element={
          <ProtectedRoute>
            <DnoInbox />
          </ProtectedRoute>
        }
      />
      <Route
        path="/audit-log"
        element={
          <ProtectedRoute>
            <AuditLog />
          </ProtectedRoute>
        }
      />
      <Route
        path="/social-inbox"
        element={
          <ProtectedRoute>
            <SocialInbox />
          </ProtectedRoute>
        }
      />
      <Route
        path="/social-notifications"
        element={
          <ProtectedRoute>
            <SocialNotifications />
          </ProtectedRoute>
        }
      />
      <Route
        path="/portal-chat"
        element={
          <ProtectedRoute>
            <PortalChat />
          </ProtectedRoute>
        }
      />
      <Route
        path="/website-chat"
        element={
          <ProtectedRoute>
            <WebsiteChat />
          </ProtectedRoute>
        }
      />
      <Route
        path="/website-leads"
        element={
          <ProtectedRoute>
            <WixLeads />
          </ProtectedRoute>
        }
      />
      <Route
        path="/email-leads"
        element={
          <ProtectedRoute>
            <EmailLeads />
          </ProtectedRoute>
        }
      />
      <Route
        path="/leads-archive"
        element={
          <ProtectedRoute>
            <LeadsArchive />
          </ProtectedRoute>
        }
      />
      <Route
        path="/whatsapp"
        element={
          <ProtectedRoute>
            <WhatsAppBusiness />
          </ProtectedRoute>
        }
      />
      <Route
        path="/contacts"
        element={
          <ProtectedRoute>
            <Contacts />
          </ProtectedRoute>
        }
      />
      <Route
        path="/permissions"
        element={
          <ProtectedRoute>
            <PermissionsPage />
          </ProtectedRoute>
        }
      />

      {/* Public pages */}
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />

      {/* Customer Portal Routes (Public) — wrapped in error boundary so a
          component crash shows a friendly message instead of a blank page */}
      <Route path="/portal/login" element={<PortalErrorBoundary><CustomerPortalLogin /></PortalErrorBoundary>} />
      <Route path="/portal/register" element={<PortalErrorBoundary><CustomerPortalRegister /></PortalErrorBoundary>} />
      <Route path="/portal/setup-password" element={<PortalErrorBoundary><PortalSetupPassword /></PortalErrorBoundary>} />
      <Route path="/portal/dashboard" element={<PortalErrorBoundary><CustomerPortalDashboard /></PortalErrorBoundary>} />
      <Route path="/portal/payment-success" element={<PortalErrorBoundary><PortalPaymentSuccess /></PortalErrorBoundary>} />
      <Route path="/portal" element={<Navigate to="/portal/login" replace />} />

      {/* Default redirect */}
      <Route path="/" element={<RootRedirect />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <AppRoutes />
          <Toaster
            position="top-right"
            toastOptions={{
              style: {
                background: 'var(--toast-bg, white)',
                border: '1px solid var(--toast-border, #e2e8f0)',
                borderRadius: '8px',
                color: 'var(--toast-color, inherit)',
              },
            }}
          />
          <CookieConsent />
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
